
import { useState } from 'react'
import {
  Box,
  HStack,
  VStack,
  Text,
  Button,
  IconButton,
} from '@chakra-ui/react'
import { Plus, Trash2 } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { SortableBlock } from './SortableBlock'
import type { PageBlock, BlockType } from '@/types/pageBuilder'

const blockTypes: { type: BlockType; label: string }[] = [
  { type: 'heading', label: 'Heading' },
  { type: 'text', label: 'Paragraph' },
  { type: 'image', label: 'Image' },
  { type: 'video', label: 'Video' },
  { type: 'button', label: 'Button' },
  { type: 'quote', label: 'Quote' },
  { type: 'list', label: 'List' },
  { type: 'code', label: 'Code' },
  { type: 'html', label: 'HTML' },
  { type: 'spacer', label: 'Spacer' },
  { type: 'divider', label: 'Divider' },
]

interface ColumnBlockProps {
  block: PageBlock
  onUpdate: (content: Record<string, any>) => void
  onDelete: () => void
  isSelected?: boolean
  onSelect: () => void
  onAddBlockToColumn: (columnIndex: number, blockType: BlockType) => void
  onDeleteBlockFromColumn: (columnIndex: number, blockId: string) => void
  onUpdateBlockInColumn: (columnIndex: number, blockId: string, content: Record<string, any>) => void
  onSelectBlockInColumn: (columnIndex: number, block: PageBlock) => void
  selectedColumnBlock: { columnIndex: number; block: PageBlock } | null
}

export function ColumnBlock({
  block,
  onUpdate,
  onDelete,
  isSelected,
  onSelect,
  onAddBlockToColumn,
  onDeleteBlockFromColumn,
  onUpdateBlockInColumn,
  onSelectBlockInColumn,
  selectedColumnBlock,
}: ColumnBlockProps) {
  const colors = useThemeColors()
  const [showAddBlock, setShowAddBlock] = useState<number | null>(null)
  const columnCount = block.content.columns || 2
  const columns = block.content.columnBlocks || Array.from({ length: columnCount }).map(() => [])

  const handleAddBlock = (columnIndex: number, blockType: BlockType) => {
    onAddBlockToColumn(columnIndex, blockType)
    setShowAddBlock(null)
  }

  const gapMap = {
    small: '2',
    medium: '4',
    large: '6',
  }

  return (
    <Box
      border="2px"
      borderColor={isSelected ? 'blue.500' : colors.border}
      borderRadius="md"
      bg={colors.cardBg}
      p={4}
      position="relative"
      cursor="pointer"
      onClick={onSelect}
      _hover={{
        borderColor: isSelected ? 'blue.500' : colors.border,
        boxShadow: 'sm',
      }}
    >
      {/* Header */}
      <HStack mb={3} justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" color={colors.text}>
          {columnCount} Column Layout
        </Text>
        <IconButton
          aria-label="Delete columns block"
          icon={<Trash2 size={14} />}
          size="xs"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          colorScheme="red"
        />
      </HStack>

      {/* Columns */}
      <HStack spacing={gapMap[block.content.gap as keyof typeof gapMap] || '4'} align="stretch">
        {Array.from({ length: columnCount }).map((_, columnIndex) => (
          <Box
            key={columnIndex}
            flex="1"
            minH="100px"
            bg={colors.sectionBg}
            border="1px dashed"
            borderColor={colors.border}
            borderRadius="sm"
            p={3}
            position="relative"
            onClick={(e) => e.stopPropagation()}
          >
            <Text fontSize="xs" color={colors.textMuted} mb={2} fontWeight="medium">
              Column {columnIndex + 1}
            </Text>

            {/* Column Content */}
            <VStack spacing={2} align="stretch">
              {columns[columnIndex]?.map((nestedBlock: PageBlock) => (
                <SortableBlock
                  key={nestedBlock.id}
                  block={nestedBlock}
                  onSelect={() => onSelectBlockInColumn(columnIndex, nestedBlock)}
                  onDelete={() => onDeleteBlockFromColumn(columnIndex, nestedBlock.id)}
                  isSelected={
                    selectedColumnBlock?.columnIndex === columnIndex &&
                    selectedColumnBlock?.block.id === nestedBlock.id
                  }
                />
              ))}

              {/* Add Block Button */}
              {showAddBlock === columnIndex ? (
                <Box
                  p={3}
                  border="1px"
                  borderColor={colors.border}
                  borderRadius="md"
                  bg={colors.cardBg}
                >
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="xs" fontWeight="medium" color={colors.text} mb={2}>
                      Add Block to Column {columnIndex + 1}
                    </Text>
                    <VStack spacing={1} align="stretch" maxH="200px" overflowY="auto">
                      {blockTypes.map(({ type, label }) => (
                        <Button
                          key={type}
                          size="xs"
                          variant="outline"
                          onClick={() => handleAddBlock(columnIndex, type)}
                          justifyContent="flex-start"
                        >
                          {label}
                        </Button>
                      ))}
                    </VStack>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setShowAddBlock(null)}
                      mt={2}
                    >
                      Cancel
                    </Button>
                  </VStack>
                </Box>
              ) : (
                <Button
                  size="xs"
                  variant="outline"
                  leftIcon={<Plus size={14} />}
                  onClick={() => setShowAddBlock(columnIndex)}
                  w="100%"
                >
                  Add Block
                </Button>
              )}
            </VStack>
          </Box>
        ))}
      </HStack>
    </Box>
  )
}

